import React, { FC, useEffect, useState } from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LazyImage from "@insite/mobius/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { css } from "styled-components";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import Carousel, { CarouselSlide, CarouselStyles } from "@insite/content-library/Components/Carousel";
import GridContainer, { GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";

const enum fields {
    isCarousel = "isCarousel",
    carouselItem = "carouselItem",
}

export interface AdditionalModel {
    fields: {
        title: string;
        isChecked: boolean;
    };
}

export interface CarouselModel {
    fields: {
        image: string;
        caption: string;
        title: string;
        content: string;
        additional: AdditionalModel[];
        destination: LinkFieldValue;
        openInNewWindow: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.carouselItem]: CarouselModel[];
        [fields.isCarousel]: boolean;
    };
}

export interface InfoCarouselStyles {
    carousel?: CarouselStyles;
    infoGridItem?: GridItemProps;
    container?: InjectableCss;
    boxContainer?: InjectableCss;
    additionalContainer?: InjectableCss;
    additional?: TypographyProps;
    title?: TypographyProps;
    caption?: TypographyProps;
    info?: TypographyProps;
    link?: LinkProps;
    imageContainer?: InjectableCss;
}

const infoCarouselStyle: InfoCarouselStyles = {
    carousel: {
        carouselSlideInner: {
            css: css`
                padding: 0 1px;
            `,
        },
    },
    infoGridItem: {
        css: css`
            padding: 1px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px;
                    `,
                    css`
                        padding: 20px;
                    `,
                    css`
                        padding: 20px;
                    `,
                ])}
        `,
    },
    container: {
        css: css`
            background-color: #323031;
            height: 346px;
            padding: 8px;
            box-sizing: border-box;
        `,
    },
    boxContainer: {
        css: css`
            box-sizing: border-box;
            height: 100%;
            border: 2px solid #f2b124;
            text-align: center;
            padding: 0 8px;
            transition: 0.2s all ease;
            overflow: hidden;
            padding-top: 20px;
            :hover {
                border: 2px solid #fff;
            }
        `,
    },

    caption: {
        css: css`
            font-weight: 400;
            font-size: 18px;
            line-height: 25px;
            margin-top: 10px;
            margin: 0;
        `,
    },
    title: {
        css: css`
            font-weight: 700 !important;
            font-size: 24px;
            line-height: 30px;
            margin: 0;
            text-transform: uppercase;
        `,
    },
    info: {
        css: css`
            color: #fff;
            font-weight: 400;
            font-size: 15px;
            line-height: 23px;
            margin-top: 10px;
            margin-bottom: 15px;
        `,
    },
    additionalContainer: {
        css: css``,
    },
    additional: {
        css: css`
            color: #f2b124 !important;
            font-weight: 700 !important;
            font-size: 15px !important;
            line-height: 20px !important;
            text-transform: uppercase;
        `,
    },
    link: {
        css: css`
            background-color: transparent;
            width: 100%;
            color: #fff;
            font-weight: normal;
            font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
            ${TypographyStyle} {
                background-color: transparent;
                width: 100%;
                color: #fff;
                font-weight: normal;
            }
        `,
    },

    imageContainer: {
        css: css`
            margin-left: 10%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0;
                    `,
                    css`
                        margin-left: 0;
                    `,
                    css`
                        margin-left: 0;
                    `,
                ])}
        `,
    },
};

const GenrateUrl: FC<{ link: LinkFieldValue, openInNewWindow?: boolean }> = props => {
    const url = useGetLink(props.link);
    return (
        <Link href={url.url} {...styles.link} target={props.openInNewWindow ? "_blank" : "_self"}>
            {props.children}
        </Link>
    );
};

type InfoProp = {
    item: CarouselModel;
};

const InfoCard: FC<InfoProp> = ({ item }) => {
    return (
        <GenrateUrl link={item.fields.destination} openInNewWindow={item.fields.openInNewWindow}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.boxContainer}>
                    <StyledWrapper>
                        <LazyImage src={item.fields.image} />
                        <Typography as="div" {...styles.caption}>
                            {item.fields.caption}
                        </Typography>
                        <Typography variant="h2" {...styles.title}>
                            {item.fields.title}
                        </Typography>
                        <Typography variant="p" {...styles.info}>
                            {item.fields.content}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.additionalContainer}>
                        {item.fields.additional &&
                            item.fields.additional.map(item => (
                                <Typography variant="p" {...styles.additional} key={item.fields.title}>
                                    {item.fields.isChecked && "✔"} {item.fields.title}
                                </Typography>
                            ))}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </GenrateUrl>
    );
};

const styles = infoCarouselStyle;
const InfoCarousel = ({ fields }: OwnProps) => {
    const [slides, setSlides] = useState<CarouselSlide[]>([]);

    if (!fields.isCarousel) {
        return (
            <GridContainer
                css={css`
                    ${GridOffset} {
                        margin: 0;
                        width: 100%;
                    }
                `}
            >
                {fields.carouselItem &&
                    fields.carouselItem.map(item => (
                        <GridItem width={[12, 12, 3, 3, 3]} {...styles.infoGridItem} key={item.fields.title}>
                            <InfoCard item={item} />
                        </GridItem>
                    ))}
            </GridContainer>
        );
    }

    const mapToSlides = () =>
        fields.carouselItem &&
        fields.carouselItem.map(item => ({
            id: Math.random().toString(),
            renderComponent: function ProductCarouselProductCondensedRenderer() {
                return <InfoCard item={item} />;
            },
        }));

    useEffect(() => {
        setSlides(mapToSlides);
    }, [fields]);
    return (
        <Carousel id="InfoCarausel" extendedStyles={styles.carousel} maxNumberOfColumns={4} title="" slides={slides} />
    );
};

const InfoCarouselModule: WidgetModule = {
    component: InfoCarousel,
    definition: {
        displayName: "Home Page Info Carousel",
        fieldDefinitions: [
            {
                name: "isCarousel",
                displayName: "Show Item in Carousel",
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
            },
            {
                name: fields.carouselItem,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Icon Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },

                    {
                        name: "caption",
                        displayName: "Caption",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "content",
                        displayName: "Content",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        displayName: "Link Url",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isRequired: true,
                    },
					{
						name: "openInNewWindow",
						editorTemplate: "CheckboxField",
						defaultValue: false,
					},
                    {
                        name: "additional",
                        displayName: "Additional text",
                        editorTemplate: "ListField",
                        getDisplay: (item: HasFields) => {
                            return item.fields.title;
                        },
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "title",
                                displayName: "Title",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },

                            {
                                name: "isChecked",
                                editorTemplate: "CheckboxField",
                                defaultValue: true,
                            },
                        ],
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default InfoCarouselModule;
